import { DataClass, load } from 'scrivito';
import { z } from 'zod';

import { MAX_ITEMS } from './config';
import {
  formatDataItemDetails,
  formatDataItemSummary,
} from './format_data_item';
import { resolveTool } from './tool_integration';

export function defineCRUDToolsV2(dataClasses: DataClass[]) {
  const classNames = dataClasses
    .map((dataClass) => dataClass.name())
    .filter((n): n is string => n !== null);

  return [
    resolveTool({
      name: 'get',

      description: 'Get the details of a specific data item',

      parameters: z.object({
        dataClass: z.enum(classNames).describe('The kind of data to fetch.'),
        ids: z
          .array(z.string())
          .describe('a list of ids for the items to get.'),
      }),

      run: async (params) => {
        const DataKlass = dataClassByName(params.dataClass);

        const { ids } = params;

        const items = await Promise.all(
          ids.map((id) => load(() => DataKlass.get(id))),
        );

        const data = await Promise.all(
          items.filter((item) => !!item).map(formatDataItemDetails),
        );
        // eslint-disable-next-line no-console
        console.log(data);
        return data;
      },
    }),

    resolveTool({
      name: 'find',

      description: `
        Find data for the given data class.
        The results contain a summary for each item.
        In order to get the details of a specific item, use the 'get' tool.
      `,

      parameters: z.object({
        dataClass: z.enum(classNames).describe('The kind of data to fetch.'),
        filter: z
          .object({
            attribute: z
              .string()
              .describe('the attribute which must have the given value'),
            value: z
              .union([z.string(), z.boolean()])
              .describe('the value that the attribute must have'),
          })
          .optional(),
        order: z
          .object({
            attribute: z.string().describe('the attribute to sort on'),
            direction: z.enum(['asc', 'desc']),
          })
          .optional(),
        search: z
          .string()
          .describe('search for keywords in the full text.')
          .optional(),
      }),

      run: async (params) => {
        const DataKlass = dataClassByName(params.dataClass);

        const order = params.order;
        const filter = params.filter;

        const dataScope = DataKlass.all().transform({
          filters: filter && { [filter.attribute]: filter.value },
          order: order && [[order.attribute, order.direction]],
          limit: MAX_ITEMS,
          search: params.search,
        });

        const items = await load(() => dataScope.take());
        const data = await Promise.all(items.map(formatDataItemSummary));
        // eslint-disable-next-line no-console
        console.log(data);
        return data;
      },
    }),

    resolveTool({
      name: 'create',

      description: 'Create a new item of the given data class.',

      parameters: z.object({
        dataClass: z
          .enum(classNames)
          .describe('The data class to create an item for.'),
        data: z
          .array(
            z.object({
              attribute: z.string().describe('the name of the attribute'),
              value: z.string().describe('the value to set for the attribute'),
            }),
          )
          .describe(
            'an list of attribute values that should be set for the new item.',
          ),
      }),

      run: async (params) => {
        const DataKlass = dataClassByName(params.dataClass);

        const data = Object.fromEntries(
          params.data.map(({ attribute, value }) => [attribute, value]),
        );

        const item = await DataKlass.create(data);

        return formatDataItemDetails(item);
      },
    }),
  ];

  function dataClassByName(name: string) {
    const DataKlass = dataClasses.find((klass) => klass.name() === name);

    if (!DataKlass) {
      throw new Error(`unknown data class ${name}`);
    }

    return DataKlass;
  }
}
